import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateConfig } from "../../../../store/slices/User/Placeholder/placeholderSlice";
import { toast } from "react-toastify";
import { createPlaceholder } from "../api";
import { useGetPlaceholderBasicDataQuery } from "../../../../store/queries/User";
import useValidations from "../../../../utils/hooks/useValidations";
import dayjs from "dayjs";

const useAddPlaceholder = (handleCreateClick, refetch) => {
  const dispatch = useDispatch();
  const [isType, setType] = useState(null);
  const { englishRegex, arabicRegex } = useValidations();
  const { data: basicData = {}, isLoading: basicLoading } =
    useGetPlaceholderBasicDataQuery();
  const { isEdit, editPlaceholderData } = useSelector(
    (state) => state.placeholder
  );

  const { isLoading } = useSelector((state) => state.placeholder);
  const validation = Yup.object({
    search_text: Yup.string()
      .trim()
      .matches(englishRegex, "Enter valid Search Text")
      .required("Enter Search Text"),
    alias_search_text: Yup.string()
      .trim()
      .matches(arabicRegex, "Enter valid Search Text")
      .required("Enter Search Text"),
    search_type: Yup.object().required("Select type"),
    start_time: isType === 2 && Yup.string().required("Please select start time"),
    end_time:
      isType === 2 &&
      Yup.mixed()
        .nullable()
        .test("is-valid-or-null", "Invalid time", function (value) {
          const { start_time } = this.parent;
          if (!start_time || value === null) return true; // Skip if start_time is not set
          return dayjs.isDayjs(value); // Ensure to_hour is dayjs if set
        })
        .test(
          "required-if-from-hour",
          "End time is required",
          function (end_time) {
            const { start_time } = this.parent;
            // Only require end_time if start_time is set
            if (start_time && (end_time === null || end_time === undefined)) {
              return false; // end_time is required in this case
            }
            return true; // Not required otherwise
          }
        )
        .test(
          "is-after-from-hour",
          "End time must be at least 1 minute after From hour",
          function (end_time) {
            const { start_time } = this.parent;

            if (!dayjs.isDayjs(start_time) || !dayjs.isDayjs(end_time)) {
              return true; // Skip test if invalid
            }

            return end_time.diff(start_time, "minute") >= 1;
          }
        ),
  });

  const formik = useFormik({
    initialValues: {
      search_text: "",
      alias_search_text: "",
      status: 2,
      search_type: "",
      start_time: null,
      end_time: null,
    },
    validationSchema: validation,
    onSubmit: (values) => {
      const formattedFromHour = values.start_time
        ? values.start_time.format("hh:mm A")
        : null;
      const formattedToHour = values.end_time
        ? values.end_time.format("hh:mm A")
        : null;
      let data = {
        search_text: values?.search_text ?? "",
        alias_search_text: values?.alias_search_text ?? "",
        status: values?.status ?? 2,
        search_type: values?.search_type?.id ?? "",
        start_time: formattedFromHour,
        end_time: formattedToHour,
      };
      let newObj = Object.entries(data);
      let subData = newObj
        .filter(
          (item) => item[1] !== undefined && item[1] !== "" && item[1] !== null
        )
        .reduce((a, v) => ({ ...a, [v[0]]: v[1] }), {});
      dispatch(
        updateConfig((state) => {
          state.isLoading = true;
        })
      );

      if (!isEdit) {
        createPlaceholder(subData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(`Placeholder created successfully`);
            refetch();
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          } else if (response?.data?.status_code === 422 && response?.data?.errors) {
            Object.keys(response?.data?.errors).forEach((field) => {
              formik.setFieldError(field, response?.data?.errors[field]);
            });
            dispatch(
              updateConfig((state) => {
                state.isLoading = false;
              })
            );
          } else {
            toast.error(`Something went wrong!`);
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          }
        });
      } else {
        let updatePlaceholderData = {
          search_id: editPlaceholderData?._id,
          ...subData,
        };

        createPlaceholder(updatePlaceholderData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(`Placeholder updated successfully`);
            refetch();
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
                state.editPlaceholderData = {};
              })
            );
          } else if (response?.data?.status_code === 422 && response?.data?.errors) {
            Object.keys(response?.data?.errors).forEach((field) => {
              formik.setFieldError(field, response?.data?.errors[field]);
            });
            dispatch(
              updateConfig((state) => {
                state.isLoading = false;
              })
            );
          } else {
            toast.error(`Something went wrong!`);
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          }
        });
      }
    },
  });

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };
  const closeModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = false;
        state.isEdit = false;
        state.editPlaceholderData = {};
      })
    );
  };

  useEffect(() => {
    let selectVal = basicData?.data?.search_type_list?.find(
      (item) => item?.id === editPlaceholderData?.search_type
    );
    if (isEdit && editPlaceholderData) {
      formik.setValues({
        search_text: editPlaceholderData?.search_text || "",
        alias_search_text: editPlaceholderData?.alias_search_text || "",
        status: editPlaceholderData?.status || 2,
        search_type: selectVal,
        start_time: editPlaceholderData.start_time
          ? dayjs(editPlaceholderData.start_time, "hh:mm A")
          : null,
        end_time: editPlaceholderData.end_time
          ? dayjs(editPlaceholderData.end_time, "hh:mm A")
          : null,
      });
    }
    // eslint-disable-next-line
  }, [isEdit, editPlaceholderData, basicLoading]);

  const handleTypeChange = (e) => {
    formik?.setFieldValue("search_type", e);
    setType(e?.value);
      if (!isEdit) {
    formik?.setFieldValue("start_time", "");
    formik?.setFieldValue("end_time", "");
      }
  };

  useEffect(() => {
    if (!formik.values.start_time) {
      formik.setFieldValue("end_time", null);
    }
  }, [formik.values.start_time]);

  return {
    isEdit,
    formik,
    isLoading,
    basicData,
    editPlaceholderData,
    handleTypeChange,
    getFieldError,
    closeModal,
  };
};

export default useAddPlaceholder;
